/**
 * Voice Reference Uploader
 *
 * Reusable upload field for a character's voice reference clip. Used inside the
 * Character Studio "create" form (next to image + identity) and by the voice
 * manage subview for existing characters.
 *
 * The clip is validated (audio, 2s–15s), uploaded to the user's base storage
 * immediately, and the returned relative blob path is reported to the parent
 * via `onUploaded`. Playback re-signs that stored path with the user-level SAS
 * token.
 */

import React, { useCallback, useRef, useState } from 'react';
import { CharacterVoiceReference } from '../../types/RiggingTypes';
import { buildVoiceReference } from '../../services/riggedCharacterService';
import {
  MAX_VOICE_REFERENCE_SECONDS,
  MIN_VOICE_REFERENCE_SECONDS,
  VOICE_REFERENCE_ACCEPT,
  isSupportedVoiceReferenceFile,
  probeAudioDuration,
  uploadCharacterVoiceReference,
  useCharacterVoiceUrl,
} from '../../services/characterStorageService';
import { Mic, Upload, Delete, GraphicEq } from '@mui/icons-material';

interface VoiceReferenceUploaderProps {
  /** Relative blob path of the currently stored voice clip. */
  voiceUrl: string | null;
  /** Optional display file name (kept in the `audio_reference` asset). */
  fileName?: string;
  /** Optional measured duration in seconds (max 15s). */
  durationSeconds?: number;
  /** Called after a clip has been validated + stored; contains the blob path. */
  // eslint-disable-next-line no-unused-vars
  onUploaded: (voiceReference: CharacterVoiceReference) => void;
  /** Called when the attached clip should be removed. */
  onRemove: () => void;
  /** Optional display name used in the helper copy. */
  characterName?: string;
}

const formatDuration = (seconds?: number): string => {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return '';
  return `${seconds.toFixed(1)}s`;
};

const VoiceReferenceUploader: React.FC<VoiceReferenceUploaderProps> = ({
  voiceUrl,
  fileName,
  durationSeconds,
  onUploaded,
  onRemove,
  characterName,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playableUrl = useCharacterVoiceUrl(voiceUrl);

  const openUploadDialog = () => {
    setUploadError(null);
    fileInputRef.current?.click();
  };

  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setUploadError(null);

      if (!isSupportedVoiceReferenceFile(file)) {
        setUploadError(
          'Please select an audio file (mp3, wav, m4a, ogg, flac, ...).'
        );
        event.target.value = '';
        return;
      }

      const token = localStorage.getItem('authToken');
      if (!token) {
        setUploadError('Please log in to upload a voice reference.');
        event.target.value = '';
        return;
      }

      setIsUploading(true);

      try {
        // Enforce the 15-second limit up front so oversized clips are rejected
        // before they are written to storage. A tiny tolerance avoids rejecting
        // a clip that is exactly 15.00s due to float metadata drift.
        const duration = await probeAudioDuration(file);
        if (duration > MAX_VOICE_REFERENCE_SECONDS + 0.05) {
          setUploadError(
            `Voice reference clips must be ${MAX_VOICE_REFERENCE_SECONDS} seconds or shorter (this file is ${duration.toFixed(
              1
            )}s).`
          );
          return;
        }

        // Enforce the video model's 2-second minimum up front: shorter clips
        // are rejected by generation, so refuse them before storage. No
        // tolerance here — borderline files would only fail later anyway.
        if (duration < MIN_VOICE_REFERENCE_SECONDS) {
          setUploadError(
            `Voice reference clips must be at least ${MIN_VOICE_REFERENCE_SECONDS} seconds (this file is ${duration.toFixed(
              1
            )}s) or the video model cannot use them.`
          );
          return;
        }

        const location = await uploadCharacterVoiceReference(file, token);
        onUploaded(buildVoiceReference(location, file.name, duration));
      } catch (error) {
        console.error('Failed to upload voice reference:', error);
        setUploadError('Failed to upload voice reference. Please try again.');
      } finally {
        setIsUploading(false);
        event.target.value = '';
      }
    },
    [onUploaded]
  );

  return (
    <div className="voice-uploader">
      <input
        ref={fileInputRef}
        type="file"
        accept={VOICE_REFERENCE_ACCEPT}
        onChange={handleFileUpload}
        disabled={isUploading}
        style={{ display: 'none' }}
      />

      <div className="voice-uploader-header">
        <Mic style={{ fontSize: 18, color: '#7be7f0' }} />
        <span>Voice reference</span>
      </div>

      <p className="voice-uploader-copy">
        {characterName
          ? `A short ${MAX_VOICE_REFERENCE_SECONDS}-second clip that tells us how “${characterName}” sounds.`
          : `Attach a ${MAX_VOICE_REFERENCE_SECONDS}-second audio clip as the character's voice.`}
      </p>

      {voiceUrl ? (
        <div className="voice-uploader-item">
          <div className="voice-uploader-item-row">
            <GraphicEq style={{ fontSize: 18 }} />
            <div className="voice-reference-file-info">
              <span className="voice-reference-file-name">
                {fileName || 'Voice reference'}
              </span>
              {formatDuration(durationSeconds) && (
                <span className="voice-reference-file-duration">
                  {formatDuration(durationSeconds)}
                </span>
              )}
            </div>
          </div>

          {playableUrl && (
            <audio
              ref={audioRef}
              controls
              src={playableUrl}
              className="voice-reference-player"
            />
          )}

          <div className="voice-uploader-actions">
            <button
              className="studio-action secondary"
              onClick={openUploadDialog}
              disabled={isUploading}
            >
              <Upload style={{ fontSize: 18 }} />
              {isUploading ? 'Uploading...' : 'Replace clip'}
            </button>
            <button
              className="studio-action secondary voice-remove"
              onClick={onRemove}
              disabled={isUploading}
            >
              <Delete style={{ fontSize: 18 }} />
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div className="voice-uploader-empty">
          <GraphicEq style={{ fontSize: 32, opacity: 0.5 }} />
          <p>No voice reference yet.</p>
          <button
            className="studio-action primary"
            onClick={openUploadDialog}
            disabled={isUploading}
          >
            <Upload style={{ fontSize: 18 }} />
            {isUploading
              ? 'Uploading...'
              : `Upload a ${MAX_VOICE_REFERENCE_SECONDS}s voice clip`}
          </button>
        </div>
      )}

      {uploadError && <div className="voice-reference-error">{uploadError}</div>}

      <p className="voice-uploader-hint">
        {MIN_VOICE_REFERENCE_SECONDS}–{MAX_VOICE_REFERENCE_SECONDS} seconds —
        mp3, wav, m4a, ogg or similar audio formats.
      </p>
    </div>
  );
};

export default VoiceReferenceUploader;